import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SiteSettingsContext } from './SiteSettingsContext';
import { customerApiErrorMessage } from './storefront/customerFacingErrors';
import { getMergedStoreCopy } from './storeCopy';
import PageSeoHelmet from './PageSeoHelmet';
import { CONTACT_COPY_DEFAULTS } from './content/pageCopyDefaults';
import { mergeContactCopy } from './content/siteCopyHelpers';
import {
  sanitizePhoneInput,
  validateCheckoutEmail,
  validateOptionalIndianMobile,
} from './checkout/contactValidation';
import './contact-page.css';

function ContactForm({ contact }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [feedback, setFeedback] = useState({ type: '', text: '' });

  const onSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: '', text: '' });
    setEmailError('');
    setPhoneError('');

    const emailNorm = validateCheckoutEmail(email);
    if (!emailNorm.ok) {
      setEmailError(emailNorm.error);
      setFeedback({ type: 'danger', text: emailNorm.error });
      return;
    }
    const phoneNorm = validateOptionalIndianMobile(phone);
    if (!phoneNorm.ok) {
      setPhoneError(phoneNorm.error);
      setFeedback({ type: 'danger', text: phoneNorm.error });
      return;
    }

    setBusy(true);
    try {
      const r = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email: emailNorm.email,
          phone: phoneNorm.phone,
          message,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setFeedback({
          type: 'danger',
          text: customerApiErrorMessage(d.error, contact.form_error_message),
        });
        return;
      }
      setFeedback({ type: 'success', text: d.message || contact.form_success_message });
      setName('');
      setEmail('');
      setPhone('');
      setMessage('');
      setEmailError('');
      setPhoneError('');
    } catch {
      setFeedback({ type: 'danger', text: contact.form_error_network });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="contact-form-shell">
      <span className="contact-form-shell__badge">{contact.form_badge}</span>
      <h2 id="contact-form-heading" className="contact-form-shell__title">
        {contact.form_title}
      </h2>
      <p className="contact-form-shell__subtitle">{contact.form_subtitle}</p>
      <form onSubmit={onSubmit} noValidate aria-labelledby="contact-form-heading">
        {feedback.text ? (
          <div className={`alert alert-${feedback.type} py-2 small mb-3`} role="alert">
            {feedback.text}
          </div>
        ) : null}
        <div className="row g-3">
          <div className="col-md-6">
            <div className="checkout-field">
              <label className="checkout-field__label" htmlFor="contact-name">
                {contact.form_label_name} <span className="checkout-field__req">*</span>
              </label>
              <input
                id="contact-name"
                className="form-control checkout-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={busy}
                autoComplete="name"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className={`checkout-field${emailError ? ' checkout-field--error' : ''}`}>
              <label className="checkout-field__label" htmlFor="contact-email">
                {contact.form_label_email} <span className="checkout-field__req">*</span>
              </label>
              <input
                id="contact-email"
                type="email"
                className="form-control checkout-input"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                onBlur={() => {
                  const n = validateCheckoutEmail(email);
                  if (email.trim() && !n.ok) setEmailError(n.error);
                  else setEmailError('');
                }}
                aria-invalid={emailError ? true : undefined}
                aria-describedby={emailError ? 'contact-email-error' : undefined}
                required
                disabled={busy}
                autoComplete="email"
              />
              {emailError ? (
                <p className="checkout-field__error" id="contact-email-error" role="alert">
                  {emailError}
                </p>
              ) : null}
            </div>
          </div>
          <div className="col-12">
            <div className={`checkout-field${phoneError ? ' checkout-field--error' : ''}`}>
              <label className="checkout-field__label" htmlFor="contact-phone">
                {contact.form_label_phone}
              </label>
              <input
                id="contact-phone"
                type="tel"
                inputMode="numeric"
                className="form-control checkout-input"
                value={phone}
                onChange={(e) => {
                  setPhone(sanitizePhoneInput(e.target.value));
                  if (phoneError) setPhoneError('');
                }}
                onBlur={() => {
                  const n = validateOptionalIndianMobile(phone);
                  if (!n.ok) setPhoneError(n.error);
                  else {
                    if (n.phone) setPhone(n.phone);
                    setPhoneError('');
                  }
                }}
                aria-invalid={phoneError ? true : undefined}
                aria-describedby={phoneError ? 'contact-phone-error' : undefined}
                disabled={busy}
                autoComplete="tel"
              />
              {phoneError ? (
                <p className="checkout-field__error" id="contact-phone-error" role="alert">
                  {phoneError}
                </p>
              ) : null}
            </div>
          </div>
          <div className="col-12">
            <div className="checkout-field">
              <label className="checkout-field__label" htmlFor="contact-message">
                {contact.form_label_message} <span className="checkout-field__req">*</span>
              </label>
              <textarea
                id="contact-message"
                className="form-control checkout-input"
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                disabled={busy}
                placeholder={contact.form_message_placeholder}
              />
            </div>
          </div>
        </div>
        <button type="submit" className="btn btn-primary store-cta rounded-pill px-4 mt-3" disabled={busy}>
          {busy ? contact.form_submit_busy : contact.form_submit}
        </button>
      </form>
    </div>
  );
}

function ContactHero({ site, contact }) {
  return (
    <header className="contact-hero-banner">
      <div className="contact-hero-banner__inner">
        <p className="contact-hero-banner__eyebrow">{contact.eyebrow}</p>
        <h1 className="contact-hero-banner__title">{contact.hero_title}</h1>
        <p className="contact-hero-banner__subtitle handwritten">{contact.hero_subtitle}</p>
        <div className="contact-hero-banner__actions">
          <a
            href={site.whatsapp_href}
            className="btn btn-primary btn-lg rounded-pill px-4"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="contact-hero-banner__wa-icon" aria-hidden="true">
              💬
            </span>
            {contact.hero_whatsapp_button}
          </a>
          <Link to="/shop" className="btn btn-outline-light btn-lg rounded-pill px-4">
            {contact.hero_shop_button}
          </Link>
        </div>
      </div>
    </header>
  );
}

function ContactChannels({ site, contact }) {
  const cards = contact.cards || CONTACT_COPY_DEFAULTS.cards;

  const channels = [
    {
      key: 'phone',
      card: cards[0],
      href: site.tel_href,
      action: site.phone_display,
      external: false,
    },
    {
      key: 'email',
      card: cards[1],
      href: site.email_href,
      action: site.email,
      external: false,
    },
    {
      key: 'location',
      card: cards[2],
      href: site.location_line
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(site.location_line)}`
        : null,
      action: site.location_line,
      external: true,
    },
    {
      key: 'whatsapp',
      card: cards[3],
      href: site.whatsapp_href,
      action: cards[3]?.link_label || 'Chat with us',
      external: true,
    },
  ];

  return (
    <section aria-labelledby="contact-methods-heading">
      <div className="contact-section-head">
        <h2 id="contact-methods-heading" className="contact-section-head__title">
          {contact.reach_title}
        </h2>
        <p className="contact-section-head__lead">{contact.reach_subtitle}</p>
      </div>
      <div className="contact-channel-grid">
        {channels.map((ch) => (
          <ContactChannelCard key={ch.key} channel={ch} />
        ))}
      </div>
    </section>
  );
}

function ContactChannelCard({ channel }) {
  const { card, href, action, external } = channel;
  if (!card?.title) return null;

  const inner = (
    <>
      <span className="contact-channel-card__icon" aria-hidden="true">
        {card.icon}
      </span>
      <div className="contact-channel-card__body">
        <h3 className="contact-channel-card__title">{card.title}</h3>
        <p className="contact-channel-card__desc">{card.desc}</p>
        {action ? (
          <span className="contact-channel-card__action">{action}</span>
        ) : null}
      </div>
    </>
  );

  if (!href) {
    return <div className="contact-channel-card contact-channel-card--static">{inner}</div>;
  }

  return (
    <a
      href={href}
      className="contact-channel-card"
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {inner}
    </a>
  );
}

function ContactHours({ site, contact }) {
  return (
    <aside className="contact-hours-panel" aria-labelledby="contact-hours-heading">
      <h2 id="contact-hours-heading" className="contact-hours-panel__title">
        {contact.hours_title}
      </h2>
      <div className="contact-hours-panel__row">
        <span className="contact-hours-panel__day">{contact.hours_day_weekdays}</span>
        <span className="contact-hours-panel__time">{site.hours_mon_fri}</span>
      </div>
      <div className="contact-hours-panel__row">
        <span className="contact-hours-panel__day">{contact.hours_day_saturday}</span>
        <span className="contact-hours-panel__time">{site.hours_sat}</span>
      </div>
      <div className="contact-hours-panel__row">
        <span className="contact-hours-panel__day">{contact.hours_day_sunday}</span>
        <span className="contact-hours-panel__time">{site.hours_sun}</span>
      </div>
      {site.hours_note ? <p className="contact-hours-panel__note">{site.hours_note}</p> : null}
    </aside>
  );
}

function ContactStudioBand({ site, contact }) {
  const instagram = String(site.instagram_url || '').trim();
  const location = String(site.location_line || '').trim();
  const store = getMergedStoreCopy(site);
  const madeIn = String(store.footer_made_in || '').trim();
  const hasLinks = location || instagram || site.whatsapp_href;

  if (!hasLinks) return null;

  return (
    <section className="contact-studio-band" aria-label="Studio location and social">
      <div className="contact-studio-band__inner">
        {location ? (
          <div className="contact-studio-band__pin">
            <span className="contact-studio-band__pin-icon" aria-hidden="true">
              📍
            </span>
            <span>
              {location}
              {madeIn ? ` · ${madeIn}` : ''}
            </span>
          </div>
        ) : null}
        <div className="contact-studio-band__links">
          {location ? (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`}
              className="contact-studio-band__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {contact.studio_maps_label}
            </a>
          ) : null}
          {instagram ? (
            <a
              href={instagram}
              className="contact-studio-band__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {contact.studio_instagram_label}
            </a>
          ) : null}
          {site.whatsapp_href ? (
            <a
              href={site.whatsapp_href}
              className="contact-studio-band__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {contact.studio_whatsapp_label}
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const { settings: site } = useContext(SiteSettingsContext);
  const contact = mergeContactCopy(site.page_copy?.contact);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="contact-page">
      <PageSeoHelmet page="contact" />

      <ContactHero site={site} contact={contact} />

      <div className="container store-page__container contact-main">
        <div className="contact-layout">
          <aside className="contact-layout__aside">
            <ContactChannels site={site} contact={contact} />
            <ContactHours site={site} contact={contact} />
          </aside>
          <div className="contact-layout__form">
            <ContactForm contact={contact} />
          </div>
        </div>
      </div>

      <ContactStudioBand site={site} contact={contact} />
    </div>
  );
}

export default Contact;
